/**
 * PanelErrorBoundary — error boundary par panneau.
 *
 * @remarks
 * Si une feature crashe, **seul son panneau** affiche un fallback — l'app
 * reste utilisable. C'est ce qui manquait totalement dans le legacy : un
 * crash dans `EvaluationDocumentExplorerView` faisait remonter l'erreur au
 * RootErrorBoundary → toute l'app HS.
 *
 * À wrapper autour de chaque rendu de View dans Panel.tsx.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { TriangleAlert, RotateCcw } from 'lucide-react';

interface Props {
  children: ReactNode;
  /** Identifiant pour le log. */
  panelId?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class PanelErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(
      `[PanelErrorBoundary${this.props.panelId ? ` ${this.props.panelId}` : ''}] Crash:`,
      error,
      info.componentStack,
    );
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  override render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="text-fg-muted flex h-full flex-col items-center justify-center gap-3 p-6">
        <TriangleAlert className="text-warning size-8" aria-hidden />
        <p className="text-fg text-sm font-medium">Ce panneau a planté</p>
        {this.state.error && (
          <p className="max-w-xs text-center text-xs">{this.state.error.message}</p>
        )}
        <button
          type="button"
          onClick={this.reset}
          className="border-border hover:bg-bg-muted mt-2 inline-flex items-center gap-1.5 rounded-sm border px-3 py-1.5 text-xs"
        >
          <RotateCcw className="size-3.5" aria-hidden />
          Réessayer
        </button>
      </div>
    );
  }
}
